import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import lottie from 'lottie-web';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';

import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../SectionContainer';
import css from './SectionHero.module.css';
import animationData from './airplane-animation.json';

/**
 * Hero Block Component for individual expert hero slides
 *
 * Supports two data formats:
 * 1. Sharetribe default fields (from CMS):
 *    - title: "Name | Location" (separated by ' | ')
 *    - text: Bio text
 *    - media: Image
 *    - callToAction: Profile link
 * 2. Custom field format (default heroes):
 *    - name, location, bio, image, profileLink as separate fields
 */
const HeroBlock = blockProps => {
  const { title, text, media, callToAction, name, location, bio, image, profileLink } = blockProps;

  // Parse Sharetribe default fields format
  let expertName, expertLocation, expertBio, expertImage, expertProfileLink;

  if (title?.content) {
    // Sharetribe format: parse title for "Name | Location"
    const titleParts = title.content.split(' | ');
    expertName = titleParts[0]?.trim() || 'Expert Name';
    expertLocation = titleParts[1]?.trim() || 'Location';
    expertBio = text?.content || 'Expert bio';

    // Get image from media field
    if (media?.image) {
      // Sharetribe image asset format
      const imageVariant = media.image.attributes?.variants?.default ||
        media.image.attributes?.variants?.square1x ||
        media.image.attributes?.variants?.square2x ||
        Object.values(media.image.attributes?.variants || {})[3];
      expertImage = imageVariant?.url || '';
    } else if (media?.content) {
      expertImage = media.content;
    }

    // Get profile link from callToAction
    expertProfileLink = callToAction?.href || callToAction?.content || '#';
  } else {
    // Custom format (default heroes)
    expertName = name?.content || name || 'Expert Name';
    expertLocation = location?.content || location || 'Location';
    expertBio = bio?.content || bio || 'Expert bio';
    expertImage = image?.content || image || '';
    expertProfileLink = profileLink?.content || profileLink || '#';
  }

  return (
    <div className={css.expertHero}>
      <div className={css.expertInfoWrapper}>
        <div className={css.expertName}>
          {expertName}
        </div>
        <div className={css.expertLocation}>{expertLocation}</div>
        <div className={css.expertBio}>
          {expertBio}
        </div>
        <a className={css.expertProfileLink} href={expertProfileLink}>
          View Profile
        </a>
      </div>
      <div
        className={css.expertImage}
        style={{
          backgroundImage: `url(${expertImage})`,
        }}
      ></div>
    </div>
  );
};


const SectionHero = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options,
    blocks,
  } = props;

  // Use provided blocks or default heroes
  const heroBlocks = blocks && blocks.length > 0 ? blocks : [];

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  // Helper function to format text: replace \n with <br /> and highlight *word*
  const formatText = (text) => {
    if (!text) return null;

    // Split by \n to handle line breaks
    const parts = text.split('\\n');

    return parts.map((part, index) => {
      // Process each part for asterisk highlighting
      const segments = part.split(/(\*[^*]+\*)/g);
      const formattedSegments = segments.map((segment, segIndex) => {
        if (segment.startsWith('*') && segment.endsWith('*')) {
          // Remove asterisks and wrap in span with highlight class
          const highlightedText = segment.slice(1, -1);
          return <span key={segIndex} className={css.highlight}>{highlightedText}</span>;
        }
        return segment.trim();
      });

      // Add <br /> between parts (except for the last one)
      return (
        <React.Fragment key={index}>
          {formattedSegments}
          {index < parts.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  useEffect(() => {
    const anim = lottie.loadAnimation({
      container: document.getElementById('lottie-animation'),
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: animationData,
    });
    return () => anim.destroy();
  }, []);

  return (
    <>
      <SectionContainer
        id={sectionId}
        className={className}
        rootClassName={classNames(css.root, 'SectionHeroRoot', rootClassName)}
        appearance={appearance}
        options={fieldOptions}
      >
        {hasHeaderFields && (
          <div className={css.heroWrapper}>
            {/* LEFT SIDE */}
            <div className={css.heroLeft}>
              <div className={css.typingWrapper}>
                <h1 className={css.titleText}>
                  {formatText(title?.content)}
                </h1>
              </div>

              <div className={css.heroLeftDescription}>
                {formatText(description?.content)}
              </div>

              <Field
                data={callToAction}
                className={classNames(defaultClasses.ctaButton, 'gridCol2')}
                options={fieldOptions}
              />
            </div>

            {/* RIGHT SIDE */}
            <div className={css.heroRight}>
              {/* Lottie plane animation */}
              <div id="lottie-animation" className={css.lottieAnimation}></div>

              {/* Decorative floating shapes */}
              <div className={css.decorations}>
                <span className={css.star}></span>
                <span className={css.circleBlue}></span>
                <span className={css.circleRed}></span>
                <span className={css.circlePurple}></span>

              </div>
            </div>
          </div>
        )}

        {/* Expert carousel with SwiperJS */}
        <div className={css.expertCarouselContainer}>
          <Swiper
            modules={[Autoplay, EffectFade, Pagination]}
            effect="fade"
            fadeEffect={{
              crossFade: true
            }}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            pagination={{
              clickable: true,
              bulletClass: css.swiperBullet,
              bulletActiveClass: css.swiperBulletActive,
            }}
            speed={1000}
            loop={true}
            className={css.expertSwiper}
          >
            {heroBlocks.map((hero, index) => (
              <SwiperSlide key={hero.blockId || index}>
                <HeroBlock {...hero} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </SectionContainer>
    </>
  );
};

export default SectionHero;

